import React, { useMemo, useCallback, useState } from "react";

import {
  GiftCardForm,
  type GiftCardFormProps,
  type GiftCardFormData,
  Image,
  currencyFromMoney,
} from "@bared/ui";

import RichText from "../RichText";
import { BackgroundContainer } from "@components/Styled/Container";
import { Sections } from "@components/Sections/Sections";
import { clsx } from "clsx";

import { Container } from "./Shared";

import { useCartAdd } from "~/hooks/useCart";

import { useProduct } from "~/templates/product/useProduct";
import { useSite } from "@hooks/useSite";

const Description = (props: any) => (
  <RichText
    {...props}
    className={clsx(props.className, "mb-mini block [&_p]:text-left")}
  />
);

export function GiftCard() {
  const { productVariants, shopifyProduct, productName, page, handle } =
    useProduct();
  const site = useSite();

  const [formValues, setFormValues] = useState<Partial<GiftCardFormData>>({});

  const selectedVariant = useMemo(
    () =>
      formValues.merchandiseId
        ? (productVariants.find(
            (variant) => variant.id === formValues.merchandiseId,
          ) ?? null)
        : null,
    [productVariants, formValues.merchandiseId],
  );

  const selectedVariantFormattedPrice = useMemo(
    () =>
      selectedVariant
        ? currencyFromMoney(selectedVariant.price).format()
        : null,
    [selectedVariant],
  );

  const { addToCart } = useCartAdd();

  const [activeIndex, setActiveIndex] = useState(0);

  const images = shopifyProduct.images.nodes;

  const activeImage = useMemo(
    () => images[activeIndex] ?? null,
    [images, activeIndex],
  );

  const onSubmit = useCallback<GiftCardFormProps["onSubmit"]>(
    async (data) => {
      await addToCart({
        quantity: 1,
        merchandiseId: data.merchandiseId,
        attributes: [
          {
            key: "From",
            value: data.senderName,
          },
          {
            key: "Recipient email",
            value: data.recipientEmail,
          },
          {
            key: "Recipient name",
            value: data.recipientName,
          },
          {
            key: "Send on",
            value: data.sendDate,
          },
          {
            key: "__shopify_send_gift_card_to_recipient",
            value: "true",
          },
          ...(!!data.message
            ? [
                {
                  key: "Message",
                  value: data.message,
                },
              ]
            : []),
          ...(activeImage
            ? [
                {
                  key: "_image",
                  value: activeImage.url,
                },
              ]
            : []),
        ],
      });
    },
    [addToCart, activeImage, activeIndex],
  );

  return (
    <>
      <BackgroundContainer background="light">
        <Container width="full" className="py-giga">
          <div className="flex w-full flex-wrap md:flex-nowrap md:gap-x-mini">
            <div className="md:w-4/10 md:px-0 2xl:pr-0 z-10 w-full px-midi md:mx-auto md:pb-midi md:pt-micro 2xl:px-tera">
              <h1 className="mb-mini font-display text-[2rem] leading-[2.5rem] md:text-[2.75rem] md:leading-[3.25rem]">
                {productName}
              </h1>
              <Description html={shopifyProduct.descriptionHtml} />
              {handle === "holiday-gift-card" && (
                <div className="mb-mini">
                  {["USD", "NZD", "AUD", "SGD"].includes(site.currencyCode) && (
                    <>
                      <p>Buy a $125 Gift Card for $100</p>
                      <p>Buy a $200 Gift Card for $150</p>
                      <p>Buy a $350 Gift Card for $275</p>
                    </>
                  )}

                  {site.currencyCode === "GBP" && (
                    <>
                      <p>Buy a £125 Gift Card for £100</p>
                      <p>Buy a £200 Gift Card for £150</p>
                      <p>Buy a £350 Gift Card for £275</p>
                    </>
                  )}

                  {site.currencyCode === "HKD" && (
                    <>
                      <p>Buy a HK$625 Gift Card for HK$500</p>
                      <p>Buy a HK$1,000 Gift Card for HK$750</p>
                      <p>Buy a HK$1,750 Gift Card for HK$1,400</p>
                    </>
                  )}
                </div>
              )}
              <p className="mb-micro">Choose a Design</p>
              <div className="mb-mini flex gap-pico">
                {images.map((image, index) => (
                  <button
                    key={image.url}
                    onClick={() => setActiveIndex(index)}
                    className={clsx(
                      "rounded h-[55px] w-[48px] overflow-hidden",
                      activeIndex === index && "border border-primary",
                    )}
                  >
                    <Image
                      data={image}
                      sizes="48px"
                      source="shopify"
                      aspectRatio="48/55"
                      alt="Gift card image"
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <GiftCardForm
                onSubmit={onSubmit}
                variants={productVariants}
                onPreviewUpdate={setFormValues}
                className="mb-mini w-full"
              />
            </div>
            <div className="md:w-5/10 w-full md:sticky md:top-[180px] md:self-start">
              <div className="sm:px-0 mx-auto px-midi pt-mini">
                <h5 className="pb-mini text-hint tracking-heading">
                  GIFT CARD PREVIEW:
                </h5>
                <div className="border border-solid">
                  {activeImage && (
                    <Image
                      sizes="100vw"
                      source="shopify"
                      data={activeImage}
                      aspectRatio="11/12"
                      className="bg-canvas"
                      alt="Gift card image"
                    />
                  )}
                  <div className="text-lg bg-white p-midi text-left">
                    <p className="mb-micro">
                      Hi{" "}
                      <span
                        className={formValues.recipientName ? "" : "italic"}
                      >
                        {formValues.recipientName || "Recipient Name"}
                      </span>
                      , You've received a {selectedVariantFormattedPrice ?? ""}{" "}
                      Bared Footwear Gift Card.
                    </p>
                    <div className="mb-micro">
                      <h5 className="mb-micro text-hint tracking-heading">
                        FROM:
                      </h5>
                      <p className={formValues.senderName ? "" : "italic"}>
                        {formValues.senderName || "Sender Name"}
                      </p>
                    </div>
                    <div>
                      <h5 className="mb-micro text-hint tracking-heading">
                        MESSAGE:
                      </h5>
                      <p className={formValues.message ? "" : "italic"}>
                        {formValues.message ||
                          "Your optional message goes here."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </BackgroundContainer>
      {page?.content && 0 < page.content.length && (
        <div className="flex flex-col items-center">
          <Sections page={page} />
        </div>
      )}
    </>
  );
}
